import { html, css } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { booleanConverter } from './common/converters';
import { TeComponent } from './common/te-component';
import { theme } from './common/theme';
import { IconName } from './te-icon';
import { pathByName } from './icons/icon.utils';
import './te-text';
import { TeProgress } from './te-progress';

/** Progress Step Component */
@customElement('te-progress-step')
export class TeProgressStep extends TeComponent {
  static styles = [
    ...TeComponent.styles,
    css`
      .te-progress-step {
        display: flex;
        align-items: center;
        justify-content: center;
        flex-direction: column;
        margin: 0 8px;
      }

      .step-circle {
        border: none;
        cursor: pointer;
        width: 32px;
        height: 32px;
        border-radius: 50%;
        background: ${theme.colors.shade0};
        color: ${theme.colors.text};
        display: flex;
        justify-content: center;
        align-items: center;
      }

      .step-divider {
        position: absolute;
        top: 14px;
        left: calc(-50% + 24px);
        right: calc(50% + 24px);
        background: ${theme.colors.shade0};
        height: 4px;
        border-radius: 2px;
      }

      .step-divider.active,
      .step-circle.active {
        background: ${theme.colors.primary.v0};
      }

      .step-circle.active {
        color: ${theme.colors.shade0};
      }

      .step-circle.disabled {
        color: ${theme.colors.shade3};
        background: ${theme.colors.shade1};
        cursor: default;
      }
    `,
  ];

  /** Text value for progress step */
  @property({ type: String }) text = '';
  /** Icon name from icon pack */
  @property({ type: String }) icon?: IconName;
  /** Progress step is disabled */
  @property({ type: Boolean, converter: booleanConverter }) disabled = false;
  /** Progress step is active */
  @property({ type: Boolean, converter: booleanConverter }) active = false;

  isStep(elem: Element): elem is TeProgressStep {
    return elem.nodeName === 'TE-PROGRESS-STEP';
  }

  getStepIndex() {
    const progress = this.parentElement as TeProgress;
    const steps = [...progress.children].filter(this.isStep);
    return steps.findIndex((step) => step === this);
  }

  handleTextClick(e: Event) {
    if (this.disabled) return;
    this.handleClick(e);
  }

  isValidIconName(value: string): value is IconName {
    return value in pathByName;
  }

  getStep() {
    if (this.icon === undefined || !this.isValidIconName(this.icon))
      return html`
        <te-text
          weight="500"
          color=${this.active
            ? theme.colors.shade0.toString()
            : this.disabled
            ? theme.colors.shade3.toString()
            : theme.colors.text.toString()}
        >
          ${this.getStepIndex() + 1}
        </te-text>
      `;

    return html`
      <te-icon
        name=${this.icon}
        size="16"
        color=${this.active ? theme.colors.shade0.toString() : theme.colors.shade5.toString()}
      ></te-icon>
    `;
  }

  render() {
    const stepCircle = [
      'step-circle',
      this.active ? 'active' : '',
      this.disabled ? 'disabled' : '',
    ].filter(Boolean);

    const stepDivider = ['step-divider', this.active ? 'active' : ''].filter(Boolean);

    return html`
      <style>
        .label-text {
          margin-top: ${this.text !== '' ? '8px' : ''};
        }
      </style>
      ${this.getStepIndex() === 0
        ? null
        : html`
            <div class="${stepDivider.join(' ')}"></div>
          `}
      <div class="te-progress-step">
        <div class="${stepCircle.join(' ')}" @click=${this.handleTextClick}>
          ${this.getStep()}
        </div>
        <te-text
          truncate
          lineClamp="2"
          class="label-text"
          align="center"
          color=${this.active
            ? theme.colors.primary.v0.toString()
            : this.disabled
            ? theme.colors.shade3.toString()
            : theme.colors.text.toString()}
        >
          ${this.text}
        </te-text>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'te-progress-step': TeProgressStep;
  }
}
